'use client';

import { useEffect, useRef } from 'react';
import { Button } from '@extern/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@extern/ui/form';
import { Input } from '@extern/ui/input';
import { InputPassword } from '@extern/ui/input-password';
import { LinkButton } from '@extern/ui/link-button';
import { Separator } from '@extern/ui/separator';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'next/navigation';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { AuthGoogleButton } from '@/app/(auth)/_partials/auth-google-button';
import { AuthLastUsedButtonWrap } from '@/app/(auth)/_partials/auth-last-used-badge';
import { EMAIL_AUTHENTICATION_STRATEGIES } from '@/app/(auth)/_utils/auth-last-authentication-strategy';
import { useUtmParams } from '@/hooks/use-utm-params';
import { emailSchema } from '@/infra/schemas/auth';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';
import { useAuthEvents } from '../../_hooks/use-auth-events';
import { useLastAuthenticationStrategy } from '../../_hooks/use-last-authentication-strategy';
import { useSignIn } from '../_hooks/use-sign-in';
import { useSignInFlowStore } from '../_hooks/use-sign-in-flow-store';
const FormSchema = z.object({
  identifier: emailSchema,
  password: z.string().min(1, {
    message: 'Password is required'
  })
});
const cardClassName = 'flex w-full sm:w-[358px] max-w-full flex-col items-center gap-6 overflow-visible rounded-3xl border border-role-outline-subtle bg-white p-6';
export function SignInStartExperiment() {
  useUtmParams();
  const experimentIdentifier = useSignInFlowStore(s => s.experimentIdentifier);
  const experimentPassword = useSignInFlowStore(s_0 => s_0.experimentPassword);
  const isFormVisible = useSignInFlowStore(s_1 => s_1.isFormVisible);
  const onChange = useSignInFlowStore(s_2 => s_2.onChange);
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      identifier: experimentIdentifier,
      password: experimentPassword
    },
    mode: 'onChange'
  });
  const watchedIdentifier = useWatch({
    control: form.control,
    name: 'identifier'
  });
  const hasValidIdentifier = emailSchema.safeParse(watchedIdentifier).success;
  const searchParams = useSearchParams();
  const {
    onSubmitPassword,
    onPrepareChooseStrategy,
    onPrepareEmailCodeFirstFactor,
    onSignInWithGoogle,
    onForgotPassword,
    fetchStatus,
    errors
  } = useSignIn();
  const {
    trackAuthScreenViewed,
    trackSignInStarted,
    trackSignInEmailReturnClicked
  } = useAuthEvents();
  const lastAuthenticationStrategy = useLastAuthenticationStrategy();
  const lastTrackedScreenRef = useRef<string | null>(null);
  async function handleTryAnotherMethod(): Promise<void> {
    const identifier = form.getValues('identifier');
    const canOpenChooseStrategy = await onPrepareChooseStrategy(identifier);
    if (!canOpenChooseStrategy) {
      return;
    }
    await onPrepareEmailCodeFirstFactor();
  }
  async function handleSubmit(data: z.infer<typeof FormSchema>): Promise<void> {
    await onSubmitPassword(data.password, data.identifier);
  }
  function handleSignInWithEmail() {
    trackSignInStarted('email');
    onChange({
      isFormVisible: true
    });
  }
  useEffect(() => {
    const subscription = form.watch((value, {
      name
    }) => {
      if (name === 'identifier') {
        onChange({
          experimentIdentifier: value.identifier ?? ''
        });
      }
      if (name === 'password') {
        onChange({
          experimentPassword: value.password ?? ''
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [form, onChange]);
  useEffect(() => {
    const currentScreen = isFormVisible ? 'start_email_form' : 'start';
    if (lastTrackedScreenRef.current === currentScreen) {
      return;
    }
    lastTrackedScreenRef.current = currentScreen;
    trackAuthScreenViewed('sign_in', currentScreen, 'start');
  }, [isFormVisible, trackAuthScreenViewed]);
  return <div className="flex w-full flex-col gap-8">
      <Form {...form}>
        <form className={cardClassName} onSubmit={form.handleSubmit(handleSubmit)}>
          {isFormVisible ? <div className="flex w-full flex-col gap-4">
              <FormField control={form.control} name="identifier" render={({
            field
          }) => <FormItem>
                    <FormLabel>Your email (personal, work or edu)</FormLabel>
                    <FormControl>
                      <Input autoCapitalize="none" autoComplete="email" autoCorrect="off" data-cy="identifier" id="identifier" placeholder="joe@extern.com" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="password" render={({
            field: field_0
          }) => <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <InputPassword autoCapitalize="none" autoComplete="password" autoCorrect="off" data-cy="password" id="password" key="password" placeholder="********" type="password" {...field_0} />
                    </FormControl>
                    {errors.fields.password && <FormMessage>{errors.fields.password.message}</FormMessage>}
                  </FormItem>} />

              <div className="flex gap-2 py-4">
                <Button appearance="outlined" className="flex-1" color="info" onClick={() => {
              trackSignInEmailReturnClicked('start_email_form', 'start');
              onChange({
                isFormVisible: false
              });
            }} type="button">
                  Return
                </Button>
                <Button appearance="filled" aria-label="Sign in with Email" className="flex-1" color="info" data-cy="sign-in" disabled={fetchStatus === 'fetching' || form.formState.isSubmitting || !form.formState.isValid} isLoading={fetchStatus === 'fetching'} type="submit">
                  Continue
                </Button>
              </div>
              <Button appearance="ghost" className="text-black" onClick={onForgotPassword} type="button">
                Forgot your password?
              </Button>

              {/* Always offered, from the email alone. It used to appear only once Clerk had listed the
                  account's factors, which takes a failed password attempt. An account created without a
                  password (passwordless sign-up) has none to fail with, so its owner had to invent one, read
                  an error, and then find this. */}
              <Button appearance="ghost" className="w-full text-black" disabled={fetchStatus === 'fetching' || !hasValidIdentifier} isLoading={fetchStatus === 'fetching'} onClick={handleTryAnotherMethod} type="button">
                Sign in with Email Code
              </Button>
            </div> : <>
              <AuthGoogleButton isLoading={fetchStatus === 'fetching'} label="Sign in with Google" onClick={onSignInWithGoogle} />

              <div className="relative flex w-full items-center justify-center p-3">
                <Separator />
                <span className="absolute mx-auto bg-white px-4 text-gray-dark text-sm">or</span>
              </div>

              <AuthLastUsedButtonWrap matchStrategy={EMAIL_AUTHENTICATION_STRATEGIES} strategy={lastAuthenticationStrategy}>
                <Button appearance="outlined" className="w-full" color="info" onClick={handleSignInWithEmail} type="button">
                  Sign in with Email
                </Button>
              </AuthLastUsedButtonWrap>
            </>}
        </form>
      </Form>
      <LinkButton appearance="ghost" className="gap-0" color="neutral" href={`/sign-up?${getAllowlistedSearchParams(searchParams)}`}>
        Don&apos;t have an account?&nbsp;
        <span className="text-role-info">Sign up</span>
      </LinkButton>
    </div>;
}